import type { DTO } from "@equidefi/shared/types";
import { ApiClient } from "./ApiClient";

class OfferingClient extends ApiClient {
  async search(params: { search?: string; page?: number }) {
    const response = await this.get("/v2/offerings", { params });
    return response.data;
  }

  async investments(id: string, filters = {}) {
    if (!id) {
      return null;
    }

    const response = await this.get(`/v2/offerings/${id}/investments`, {
      params: filters,
    });
    return response.data;
  }

  async updateSignatory(id: string, sigantoryId: string) {
    const response = await this.patch(`/v2/offerings/${id}/signatory`, {
      signatory_id: sigantoryId,
    });
    return response.data;
  }

  async legacyCreate(data: unknown) {
    const response = await this.post(`/offering`, data);
    return response.data;
  }

  async legacyGetSettings(id: string) {
    const response = await this.get(`/offering/${id}/setting`);
    return response.data;
  }

  async legacyFind(id: string) {
    const response = await this.get(`/offering/${id}`);
    return response.data;
  }

  async getFullById(id: string) {
    const response = await this.get(`/v2/offerings/${id}/full`);
    return response.data ?? {};
  }

  async getOfferingDocuments(offeringId: string) {
    const response = await this.get(`/v2/offerings/${offeringId}/documents`);
    return response.data;
  }

  async createOfferingDocument(offeringId: string, data: object) {
    const response = await this.post(
      `/v2/offerings/${offeringId}/documents`,
      data,
    );
    return response.data;
  }

  async updateOfferingDocument(
    offeringId: string,
    documentId: string,
    data: object,
  ) {
    const response = await this.patch(
      `/v2/offerings/${offeringId}/documents/${documentId}`,
      data,
    );
    return response.data;
  }

  async deleteOfferingDocument(
    offeringId: string,
    documentId: string,
    data: object,
  ) {
    const response = await this.delete(
      `/v2/offerings/${offeringId}/documents/${documentId}`,
      data,
    );
    return response.data;
  }

  async getById(id: string) {
    const response = await this.get(`/v2/offerings/${id}`);
    return response.data?.data;
  }

  async update(id: string, data: Partial<DTO.Offering>) {
    const response = await this.patch(`/v2/offerings/${id}`, data);
    return response.data;
  }

  async remove(id: string) {
    const response = await this.delete(`/v2/offerings/${id}`);
    return response.data;
  }

  async summary(id: string) {
    const response = await this.get(`/v2/offerings/${id}/summary`);
    return response.data;
  }

  async settings(id: string) {
    const response = await this.get(`/offering/${id}/setting`);
    return response.data;
  }

  async brokers(id: string) {
    const response = await this.get(`/v2/offerings/${id}/brokers`);
    return response.data;
  }

  async getTraunches(offeringId: string) {
    const response = await this.get(`/v2/offerings/${offeringId}/traunches`);
    return response.data;
  }

  async getAvailableInvestments(
    offeringId: string,
    filters: DTO.TrancheFilters,
  ) {
    const response = await this.get(
      `/v2/offerings/${offeringId}/traunches/investments`,
      {
        params: filters,
      },
    );
    return response.data;
  }

  async getTranche(id: string, offeringId: string) {
    const response = await this.get(
      `/v2/offerings/${offeringId}/traunches/${id}`,
    );
    return response.data;
  }

  async getTrancheInvestments(
    id: string,
    offeringId: string,
    filters: DTO.TrancheFilters,
  ) {
    const response = await this.get(
      `/v2/offerings/${offeringId}/tranche-investments/${id}`,
      {
        params: filters,
      },
    );
    return response.data;
  }

  async createTruanche(offeringId: string) {
    const response = await this.post(`/v2/offerings/${offeringId}/traunches`);
    return response.data;
  }

  async getTrauncheDocument(id: string, offeringId: string, document: string) {
    const response = await this.get(
      `/v2/offerings/${offeringId}/traunches/${id}/document/${document}`,
    );

    return response.data;
  }

  async authenticate(email: string, password: string, offeringId: string) {
    const response = await this.post(`/v2/offerings/${offeringId}/auth`, {
      email,
      password,
    });
    return response.data;
  }

  async register(data: Record<string, any>, offeringId: string) {
    const response = await this.post(
      `/v2/offerings/${offeringId}/register`,
      data,
    );
    return response;
  }

  async issuerOfferings(
    issuerId: string,
    params: Record<"page" | "search", string>,
  ) {
    const response = await this.get(`/v2/offerings/issuer/${issuerId}`, {
      params,
    });
    return response.data;
  }

  async userOfferings() {
    const response = await this.get("/v2/offerings/user");
    return response.data;
  }

  async exportInvestors(offeringId: string, data: object) {
    const response = await this.post(
      `/v2/offerings/${offeringId}/export`,
      data,
    );
    return response;
  }

  async offeringContacts(offeringId: string) {
    const response = await this.get(`/v2/offerings/${offeringId}/contacts`);
    return response.data;
  }

  async createOfferingContact(offeringId: string, data: object) {
    const response = await this.post(
      `/v2/offerings/${offeringId}/contacts`,
      data,
    );
    return response.data;
  }

  async updateOfferingContact(offeringId: string, data: object) {
    const response = await this.patch(
      `/v2/offerings/${offeringId}/contacts/${data?.id}`,
      data,
    );
    return response.data;
  }

  async deleteOfferingContact(offeringId: string, contactId?: string) {
    if (!contactId) return null;
    const response = await this.delete(
      `/v2/offerings/${offeringId}/contacts/${contactId}`,
    );
    return response.data;
  }

  async createOffering(issuerId: string, data: object) {
    const response = await this.post(`/v2/offerings/create`, {
      issuer_id: issuerId,
      ...data,
    });
    return response;
  }

  async getAggregations(offeringId: string) {
    const response = await this.get(`/v2/offerings/${offeringId}/aggregations`);
    return response.data;
  }
}

export default OfferingClient;
